import { gql, useQuery } from '@apollo/client';
import { Divider } from '@spaceship-fspl/components';
import {
  SuperPortfolio as GQLSuperPortfolio,
  superPortfolioInformationScalars,
} from '@spaceship-fspl/graphql';
import {
  WebSuperPortfolioPage,
  WebSuperPortfolioPageVariables,
} from '@spaceship-fspl/graphql/src/__generated__/WebSuperPortfolioPage';
import { SuperPortfolio as S8SuperPortfolioEnum } from '@spaceship-fspl/types/min/externalapi';
import {
  getHeroPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  HeaderProps,
  ImportantDocumentsPanel,
  mapNodeToHeaderProps,
  Metadata,
  MetadataProps,
  ReturnsPanel,
  RiskPanel,
  SignUpPanel,
  StickyHeader,
  SUPER_BASIC_DOCUMENTS,
  withHeroPageLayout,
} from 'components';
import {
  getHeader,
  getSuperPortfolio,
  mapSlicesToProps,
  RichText,
} from 'data/prismic';
import {
  Super_PortfolioBodyFees,
  Super_PortfolioBodyFeesFields,
  Super_PortfolioBodyFeesPrimary,
  Super_PortfolioBodyHero,
  Super_PortfolioBodyHeroPrimary,
  Super_PortfolioBodyImportant_Documents,
  Super_PortfolioBodyImportant_DocumentsPrimary,
  Super_PortfolioBodyReturns,
  Super_PortfolioBodyReturnsPrimary,
  Super_PortfolioBodyRisks,
  Super_PortfolioBodyRisksFields,
  Super_PortfolioBodyRisksPrimary,
  Super_PortfolioBodySupersignup,
  Super_PortfolioBodySupersignupPrimary,
  Super_PortfolioBodyWhere,
  Super_PortfolioBodyWhereFields,
  Super_PortfolioBodyWherePrimary,
} from 'data/prismic/types';
import { SuperPrismicPortfolio } from 'helpers/products';
import { GetStaticPaths, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { ParsedUrlQuery } from 'querystring';

import { PortfolioAllocationsPanelProps } from './portfolio-allocations-panel';
import { PortfolioFeesPanel } from './portfolio-fees-panel';
import { PortfolioHeroPanel } from './portfolio-hero-panel';
import { SuperSignUpPanel } from './sign-up-panel';

const PortfolioAllocationsPanel = dynamic<PortfolioAllocationsPanelProps>(
  import('./portfolio-allocations-panel').then(
    ({ PortfolioAllocationsPanel }) => PortfolioAllocationsPanel,
    // @see https://github.com/vercel/next.js/issues/4515#issuecomment-447570395
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as any,
  { ssr: false },
);

interface SuperPortfolioProps {
  portfolio: SuperPrismicPortfolio;
  headerProps?: HeaderProps;
  data: {
    Super_portfolioBodyMetadata?: MetadataProps;
    Super_portfolioBodyHero?: Super_PortfolioBodyHeroPrimary | null;
    Super_portfolioBodyReturns?: Super_PortfolioBodyReturnsPrimary | null;
    Super_portfolioBodyWhere?:
      | null
      | (Super_PortfolioBodyWherePrimary & {
          sectors?: Super_PortfolioBodyWhereFields[] | null;
        });
    Super_portfolioBodyFees?:
      | null
      | (Super_PortfolioBodyFeesPrimary & {
          fees?: Super_PortfolioBodyFeesFields[] | null;
        });
    Super_portfolioBodyRisks?:
      | null
      | (Super_PortfolioBodyRisksPrimary & {
          risks?: Super_PortfolioBodyRisksFields[] | null;
        });
    Super_portfolioBodyImportant_documents?: Super_PortfolioBodyImportant_DocumentsPrimary | null;
    Super_portfolioBodySupersignup?: Super_PortfolioBodySupersignupPrimary | null;
  };
}

const SuperPortfolio: NextPage<SuperPortfolioProps> = ({
  portfolio,
  headerProps,
  data: {
    Super_portfolioBodyMetadata: metadata,
    Super_portfolioBodyHero: hero,
    Super_portfolioBodyReturns: portfolioReturns,
    Super_portfolioBodyRisks: risks,
    Super_portfolioBodyImportant_documents: documents,
    Super_portfolioBodyWhere: where,
    Super_portfolioBodyFees: fees,
    Super_portfolioBodySupersignup: superSignup,
  },
}) => {
  const gqlPortfolio = portfolio.toUpperCase() as GQLSuperPortfolio;
  const resp = useQuery<WebSuperPortfolioPage, WebSuperPortfolioPageVariables>(
    gql`
      query WebSuperPortfolioPage($portfolio: SuperPortfolio!) {
        superPortfolioInformation(portfolio: $portfolio) {
          id
          ...superPortfolioInformationFields
        }
      }
      ${superPortfolioInformationScalars}
    `,
    {
      variables: {
        portfolio: gqlPortfolio,
      },
    },
  );
  const portfolioData = resp.data?.superPortfolioInformation;
  const allocationPortfolio = {
    growthx: S8SuperPortfolioEnum.Enum.GROWTHX,
    global_index: S8SuperPortfolioEnum.Enum.GLOBAL_INDEX,
    balanced: S8SuperPortfolioEnum.Enum.BALANCED,
    moderate: S8SuperPortfolioEnum.Enum.MODERATE,
  }[portfolio];

  return (
    <>
      {metadata && <Metadata {...metadata} />}
      {hero && (
        <PortfolioHeroPanel
          portfolio={portfolio}
          product={hero.product || ''}
          title={hero.title || ''}
          tagline={hero.tagline || ''}
          headerComponent={(props) => (
            <StickyHeader {...props} {...headerProps} />
          )}
        />
      )}

      {portfolioReturns && (
        <ReturnsPanel
          title={portfolioReturns.title ?? ''}
          tagline={portfolioReturns.tagline ?? ''}
          disclaimer={<RichText render={portfolioReturns.richdisclaimer} />}
          portfolio={{
            type: portfolio,
            title: portfolioData?.title ?? '',
            inceptionDate: portfolioData?.inceptionDate,
            sinceInceptionPerformancePercentage:
              portfolioData?.sinceInceptionPerformancePercentage,
            oneMonthPerformancePercentage:
              portfolioData?.oneMonthPerformancePercentage,
            threeMonthPerformancePercentage:
              portfolioData?.threeMonthPerformancePercentage,
            sixMonthPerformancePercentage:
              portfolioData?.sixMonthPerformancePercentage,
            oneYearPerformancePercentage:
              portfolioData?.oneYearPerformancePercentage,
            threeYearPerformancePercentage:
              portfolioData?.threeYearPerformancePercentage,
            performanceAsAt: portfolioData?.performanceAsAt,
          }}
        />
      )}

      {where && (
        <>
          <Divider color="neutral.050" />
          <PortfolioAllocationsPanel
            title={where.title || ''}
            assetTitle={where.asset_title || ''}
            disclaimer={where.disclaimer || ''}
            portfolio={allocationPortfolio}
          />
        </>
      )}

      {fees && (
        <>
          <Divider color="neutral.050" />
          <PortfolioFeesPanel
            backgroundColor="neutral.015"
            title={fees.title ?? ''}
            portfolioTitle={portfolioData?.title ?? ''}
            feeTableTitle={fees.fee_table_title ?? ''}
            fees={fees.fees}
            example={<RichText render={fees.rich_example} />}
            disclaimer={<RichText render={fees.richdisclaimer} />}
          />
        </>
      )}

      {risks && (
        <>
          <Divider color="neutral.050" />
          <RiskPanel
            variant="super"
            title={risks.title || ''}
            tagline={risks.tagline || ''}
            risks={risks.risks?.map((risk) => ({
              name: risk.title || '',
              description: risk.description || '',
            }))}
            disclaimer={<RichText render={risks.richdisclaimer} />}
          />
        </>
      )}

      {documents && (
        <>
          <Divider color="neutral.050" />
          <ImportantDocumentsPanel
            title={documents.title || ''}
            tagline={documents.summary || ''}
            documents={SUPER_BASIC_DOCUMENTS}
          />
        </>
      )}

      {superSignup ? (
        <SuperSignUpPanel
          portfolio={portfolio}
          title={superSignup.title}
          tagline={superSignup.tagline}
          disclaimer={
            superSignup.richdisclaimer && (
              <RichText render={superSignup.richdisclaimer} />
            )
          }
        />
      ) : (
        <SignUpPanel
          title="Invest in your future, so you can live the life you want to live"
          tagline="Get started in five minutes."
          product="super"
        />
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      { params: { portfolio: 'growthx' } },
      { params: { portfolio: 'global-index' } },
      { params: { portfolio: 'balanced' } },
      { params: { portfolio: 'moderate' } },
    ],
    fallback: false,
  };
};

interface SuperPortfolioContext extends ParsedUrlQuery {
  portfolio: 'growthx' | 'global-index' | 'balanced' | 'moderate';
}

export const getStaticProps = getHeroPageLayoutStaticProps<
  SuperPortfolioProps,
  SuperPortfolioContext
>(async (context) => {
  const portfolio = {
    growthx: 'growthx',
    'global-index': 'global_index',
    balanced: 'balanced',
    moderate: 'moderate',
  }[context?.params?.portfolio ?? 'growthx'] as SuperPrismicPortfolio;

  const portfolioId = {
    growthx: 'XtR4HxEAACoAY3YS',
    global_index: 'XtR4DREAACkAY3W6',
    balanced: 'aDmbkxIAAB4Amsk5',
    moderate: 'aDme5hIAAB4Ams8-',
  }[portfolio];

  const header = await getHeader();
  const document = await getSuperPortfolio(portfolioId);
  const data = mapSlicesToProps(document, {
    Super_portfolioBodyMetadata: (slice) =>
      getMetadataPropsFromPrismicSlice(slice),
    Super_portfolioBodyHero: (slice: Super_PortfolioBodyHero) => {
      const { title, product, tagline } = slice.primary || {};
      return {
        title,
        product,
        tagline,
      };
    },
    Super_portfolioBodyReturns: (slice: Super_PortfolioBodyReturns) => {
      const { title, tagline, richdisclaimer } = slice.primary || {};
      return {
        title,
        tagline,
        richdisclaimer,
      };
    },
    Super_portfolioBodyWhere: (slice: Super_PortfolioBodyWhere) => {
      const { title, disclaimer, sector_title, asset_title } =
        slice.primary || {};
      return {
        title,
        disclaimer,
        sector_title,
        asset_title,
        sectors: slice.fields,
      };
    },
    Super_portfolioBodyFees: (slice: Super_PortfolioBodyFees) => {
      const {
        title,
        fee_table_title,
        example_title,
        example_subtitle,
        richdisclaimer,
        rich_example,
      } = slice.primary || {};
      return {
        title,
        fee_table_title,
        example_title,
        example_subtitle,
        richdisclaimer,
        rich_example,
        fees: slice.fields,
      };
    },
    Super_portfolioBodyRisks: (slice: Super_PortfolioBodyRisks) => {
      const { title, tagline, richdisclaimer } = slice.primary || {};
      return { title, tagline, richdisclaimer, risks: slice.fields };
    },
    Super_portfolioBodyImportant_documents: (
      slice: Super_PortfolioBodyImportant_Documents,
    ) => {
      const { title, summary } = slice.primary || {};
      return {
        title,
        summary,
      };
    },
    Super_portfolioBodySupersignup: (
      slice: Super_PortfolioBodySupersignup,
    ) => ({
      ...slice.primary,
    }),
  });

  return {
    props: {
      portfolio,
      headerProps: mapNodeToHeaderProps(header),
      data,
    },
  };
});

export default withHeroPageLayout({ product: 'super', showSignUpPanel: false })(
  SuperPortfolio,
);
